/**
 * Suite and option product banners — the description's "See more".
 *
 * The section renders the first N words of the description, then the rest of
 * the text and the ellipsis as hidden and visible siblings, and this button
 * swaps them. The markup carries everything needed — both labels, the target
 * id — so the two banners share the script unchanged and there is nothing to
 * configure here.
 *
 * The swap is animated. The rest of the text runs on inline from the excerpt,
 * so it has no height of its own to transition; instead the paragraph is
 * measured before and after the swap and its height is transitioned between
 * the two, with the revealed text fading alongside (the CSS keys the fade on
 * data-suite-description-open). Both measurements are taken before anything
 * paints, so nothing flashes. Under prefers-reduced-motion the swap is
 * immediate.
 *
 * Without the script the excerpt still renders; only the button is inert.
 */
(function () {
    'use strict';

    var SECTION = '[data-section-type="suite-banner"], [data-section-type="option-banner"]';
    var ANIMATING = 'suite-banner-description-animating';

    function toArray(list) {
        return Array.prototype.slice.call(list);
    }

    function reducedMotion() {
        return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    /** The element's transition-duration, in ms — the stylesheet stays the one source of truth. */
    function durationOf(element) {
        var raw = window.getComputedStyle(element).transitionDuration || '0s';
        var value = parseFloat(raw) || 0;

        return raw.indexOf('ms') > -1 ? value : value * 1000;
    }

    function setup(section) {
        var description = section.querySelector('[data-suite-description]');

        if (!description) return;

        var toggle = description.querySelector('[data-suite-description-toggle]');
        var rest = description.querySelector('[data-suite-description-rest]');
        var ellipsis = description.querySelector('[data-suite-description-ellipsis]');

        if (!toggle || !rest) return;

        // Set while a height transition is running; calling it lands the end state
        // early, so a second click mid-flight measures from settled markup.
        var finish = null;

        function setState(open) {
            description.setAttribute('data-suite-description-open', open ? 'true' : 'false');
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            toggle.textContent = toggle.getAttribute(open ? 'data-label-less' : 'data-label-more');
        }

        /** The resting markup for a state, with everything the animation set cleared. */
        function settle(open) {
            description.classList.remove(ANIMATING);
            description.style.height = '';
            description.style.overflow = '';

            rest.hidden = !open;

            if (ellipsis) ellipsis.hidden = open;

            finish = null;
        }

        toggle.addEventListener('click', function () {
            if (finish) finish();

            var open = toggle.getAttribute('aria-expanded') !== 'true';

            if (reducedMotion()) {
                setState(open);
                settle(open);
                return;
            }

            // Both ends of the transition, measured before anything paints: the
            // height as it stands, then the height with the text in its new state.
            var from = description.getBoundingClientRect().height;

            rest.hidden = !open;

            if (ellipsis) ellipsis.hidden = open;

            var to = description.getBoundingClientRect().height;

            if (from === to) {
                setState(open);
                settle(open);
                return;
            }

            // Closing: the rest stays in the flow and fades while the box shrinks
            // over it, and the ellipsis only comes back once the height has landed.
            if (!open) {
                rest.hidden = false;

                if (ellipsis) ellipsis.hidden = true;
            }

            description.style.height = from + 'px';
            description.style.overflow = 'hidden';
            description.classList.add(ANIMATING);

            // Commit the start height before the target is set, or nothing animates.
            void description.offsetHeight;

            setState(open);
            description.style.height = to + 'px';

            // The timer covers a transitionend that never arrives.
            var timer = window.setTimeout(function () {
                if (finish) finish();
            }, durationOf(description) + 50);

            function onEnd(event) {
                if (event.target === description && event.propertyName === 'height') finish();
            }

            finish = function () {
                window.clearTimeout(timer);
                description.removeEventListener('transitionend', onEnd);
                settle(open);
            };

            description.addEventListener('transitionend', onEnd);
        });
    }

    function init(root) {
        var scope = root && root.querySelectorAll ? root : document;

        if (scope.matches && scope.matches(SECTION)) setup(scope);

        toArray(scope.querySelectorAll(SECTION)).forEach(setup);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            init(document);
        });
    } else {
        init(document);
    }

    // The theme editor re-renders sections without a full page load.
    document.addEventListener('shopify:section:load', function (event) {
        init(event.target);
    });
})();
